import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useCampaignReportDownload, useScheduledCampaigns } from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import {
  CampaignFilters,
  applyCampaignFilters,
  rowsHaveRetries,
} from '../components/CampaignFilters';
import type { CampaignRow } from '../types';

export function VoiceCampaignSchedulePage() {
  const query = useScheduledCampaigns();
  const allRows = query.data ?? [];
  const reportDownload = useCampaignReportDownload();

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const rows = useMemo(
    () => applyCampaignFilters(allRows, selectedStatuses, selectedTemplates),
    [allRows, selectedStatuses, selectedTemplates],
  );

  const hasRetries = rowsHaveRetries(rows);
  const baseTotal = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.numbersUploaded ?? 0), 0),
    [rows],
  );

  const columns = useMemo(
    () =>
      buildCampaignColumns({
        onDownloadReport: reportDownload.download,
        baseTotal,
      }),
    [reportDownload.download, baseTotal],
  );

  // Schedule view: most counters are 0 until launch — hide them by default.
  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    callsConnected: false,
    pulses: false,
    dndCount: false,
    numbersProcessed: false,
    pending: false,
    endTime: false,
    retryCount: hasRetries,
    retries: hasRetries,
  };

  return (
    <FillPage>
      <PageHeader
        title="Scheduled Campaigns"
        description="Campaigns waiting to launch or recently submitted."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              />
            </Button>
            <Button asChild>
              <Link to="/add-voice-campaign">
                <Plus className="h-4 w-4" />
                Compose campaign
              </Link>
            </Button>
          </div>
        }
      />

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:schedule:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search scheduled campaigns…"
        emptyTitle="No scheduled campaigns"
        emptyDescription="Campaigns waiting to launch will appear here."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-schedule.csv"
        pageSize={10}
        toolbar={
          <CampaignFilters
            selectedStatuses={selectedStatuses}
            selectedTemplates={selectedTemplates}
            onStatusChange={setSelectedStatuses}
            onTemplateChange={setSelectedTemplates}
          />
        }
      />
    </FillPage>
  );
}
